import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { Textarea } from '@journ/ui/textarea'
import { useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { createWorkflow } from '../application/manage-workflow'
import { workflows as workflowsPort, notify } from '../wiring'
import { useWorkflowListParams } from './use-workflow-list-params'

type Props = {
	onSuccess: () => void
}

const WorkflowCreateSheet = ({ onSuccess }: Props) => {
	const content = useIntlayer('workflows-list-page')
	const c = content.createSheet
	const { createWorkflow: isOpen, setParams } = useWorkflowListParams()

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [nameError, setNameError] = useState<string | null>(null)
	const [submitting, setSubmitting] = useState(false)

	const close = () => {
		setParams({ createWorkflow: null })
		setName('')
		setDescription('')
		setNameError(null)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (name.trim().length < 2) {
			setNameError(c.nameError.value)
			return
		}

		setNameError(null)
		setSubmitting(true)

		const result = await createWorkflow(
			workflowsPort,
			{ name: name.trim(), description: description.trim() || null },
			notify,
		)

		setSubmitting(false)

		if (result) {
			onSuccess()
			close()
		}
	}

	return (
		<Sheet open={Boolean(isOpen)} onOpenChange={close}>
			<SheetContent stack title={c.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{c.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
					<div className='flex flex-col gap-2'>
						<Label htmlFor='wf-name'>{c.name}</Label>
						<Input
							id='wf-name'
							placeholder={c.namePlaceholder.value}
							value={name}
							onChange={e => setName(e.target.value)}
							autoFocus
						/>
						{nameError && <p className='text-xs text-destructive'>{nameError}</p>}
					</div>

					<div className='flex flex-col gap-2'>
						<Label htmlFor='wf-desc'>{c.description}</Label>
						<Textarea
							id='wf-desc'
							placeholder={c.descriptionPlaceholder.value}
							value={description}
							onChange={e => setDescription(e.target.value)}
							rows={3}
						/>
					</div>

					<div className='flex justify-end gap-2 pt-2'>
						<Button type='button' variant='outline' onClick={close}>
							{c.cancel}
						</Button>
						<Button type='submit' disabled={submitting}>
							{c.create}
						</Button>
					</div>
				</form>
			</SheetContent>
		</Sheet>
	)
}

export { WorkflowCreateSheet }
